/**
 * @file
 * List/map view tab switcher shared by the dual-display views.
 *
 * @description
 * The component only renders the tab buttons; the panels live in the template
 * that embeds it. Everything is matched through data attributes so the same
 * behavior drives the real-estate search paragraph and the show-house overview
 * paragraph:
 * - [data-view-tabs-root]            the element wrapping tabs and panels
 * - [data-view-tabs]                 the tablist
 * - [data-view-tab="list"|"map"]     a tab button
 * - [data-view-tabs-panel="list"|"map"] the panel a tab controls
 *
 * Views AJAX can replace the tablist, the panels, or both, so the active mode
 * is stored per root element and re-applied on every behavior attach.
 */

((Drupal, once) => {
  'use strict';

  type ViewTabsMode = 'list' | 'map';

  const ROOT_SELECTOR = '[data-view-tabs-root]';
  const TAB_LIST_SELECTOR = '[data-view-tabs]';
  const TAB_SELECTOR = '[data-view-tab]';
  const PANEL_SELECTOR = '[data-view-tabs-panel]';
  const ACTIVE_TAB_CLASS = 'view-tabs__tab--active';
  const activeModeByRoot = new WeakMap<HTMLElement, ViewTabsMode>();
  let viewTabsId = 0;

  /**
   * Returns the mode a tab or panel refers to, when it is a supported one.
   */
  function toMode(value: string | undefined): ViewTabsMode | null {
    return value === 'list' || value === 'map' ? value : null;
  }

  /**
   * Returns the element wrapping both the tablist and the panels it controls.
   */
  function getRoot(tabList: HTMLElement): HTMLElement | null {
    return tabList.closest<HTMLElement>(ROOT_SELECTOR);
  }

  /**
   * Collects the panels of a root, keyed by the mode that activates them.
   */
  function getPanels(root: HTMLElement): Partial<Record<ViewTabsMode, HTMLElement>> {
    const panels: Partial<Record<ViewTabsMode, HTMLElement>> = {};

    root.querySelectorAll<HTMLElement>(PANEL_SELECTOR).forEach((panel) => {
      const mode = toMode(panel.dataset.viewTabsPanel);
      if (mode && !panels[mode]) {
        panels[mode] = panel;
      }
    });

    return panels;
  }

  /**
   * Ensures tabs and panels have the ARIA references needed for tab semantics.
   */
  function ensureTabReferences(tabList: HTMLElement, panels: Partial<Record<ViewTabsMode, HTMLElement>>): void {
    tabList.querySelectorAll<HTMLElement>(TAB_SELECTOR).forEach((tab) => {
      const mode = toMode(tab.dataset.viewTab);
      const panel = mode ? panels[mode] : undefined;
      if (!mode || !panel) {
        return;
      }

      if (!tab.id) {
        tab.id = `view-tabs-${mode}-tab-${++viewTabsId}`;
      }
      if (!panel.id) {
        panel.id = `view-tabs-${mode}-panel-${viewTabsId}`;
      }

      tab.setAttribute('aria-controls', panel.id);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
    });
  }

  /**
   * Activates the requested view and hides the panels of the other tabs.
   */
  function setMode(tabList: HTMLElement, mode: ViewTabsMode): void {
    const root = getRoot(tabList);
    if (!root) {
      return;
    }

    const panels = getPanels(root);
    ensureTabReferences(tabList, panels);
    activeModeByRoot.set(root, mode);

    tabList.querySelectorAll<HTMLElement>(TAB_SELECTOR).forEach((tab) => {
      const isActive = tab.dataset.viewTab === mode;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle(ACTIVE_TAB_CLASS, isActive);
    });

    (Object.keys(panels) as ViewTabsMode[]).forEach((panelMode) => {
      const panel = panels[panelMode];
      if (panel) {
        panel.hidden = panelMode !== mode;
      }
    });
  }

  /**
   * Returns the mode that should be active for the given tablist.
   */
  function getActiveMode(tabList: HTMLElement): ViewTabsMode {
    const root = getRoot(tabList);
    return (root && activeModeByRoot.get(root)) ?? 'list';
  }

  /**
   * Re-applies the active view after Views AJAX replaces a tablist or a panel.
   */
  function refreshAll(): void {
    document.querySelectorAll<HTMLElement>(TAB_LIST_SELECTOR).forEach((tabList) => {
      setMode(tabList, getActiveMode(tabList));
    });
  }

  /**
   * Handles keyboard navigation inside the tablist.
   */
  function handleKeydown(event: KeyboardEvent, tabList: HTMLElement): void {
    const tabs = Array.from(tabList.querySelectorAll<HTMLElement>(TAB_SELECTOR));
    const currentTab = event.target instanceof HTMLElement ? event.target.closest<HTMLElement>(TAB_SELECTOR) : null;
    const currentIndex = currentTab ? tabs.indexOf(currentTab) : -1;

    if (currentIndex === -1) {
      return;
    }

    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1;
        break;
      case 'ArrowRight':
        nextIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const currentMode = toMode(currentTab?.dataset.viewTab);
        if (currentMode) {
          setMode(tabList, currentMode);
        }
        return;
      }
      default:
        return;
    }

    event.preventDefault();
    const nextTab = nextIndex === null ? undefined : tabs[nextIndex];
    const nextMode = toMode(nextTab?.dataset.viewTab);
    if (nextTab && nextMode) {
      nextTab.focus();
      setMode(tabList, nextMode);
    }
  }

  /**
   * List/map tab switcher for views rendered as both a card list and a map.
   */
  Drupal.behaviors.tpViewTabs = {
    attach(context: Document | HTMLElement) {
      once('tp-view-tabs', TAB_LIST_SELECTOR, context).forEach((element) => {
        const tabList = element as HTMLElement;

        setMode(tabList, getActiveMode(tabList));

        tabList.querySelectorAll<HTMLElement>(TAB_SELECTOR).forEach((tab) => {
          tab.addEventListener('click', () => {
            const mode = toMode(tab.dataset.viewTab);
            if (mode) {
              setMode(tabList, mode);
              tab.focus();
            }
          });
        });

        tabList.addEventListener('keydown', (event) => {
          handleKeydown(event as KeyboardEvent, tabList);
        });
      });

      refreshAll();
    },
  };
})(Drupal, once);
